import React, { useEffect, useRef, useState, useMemo } from 'react';
import { Capacitor } from '@capacitor/core';
import { createPortal } from 'react-dom';
import { Room, RoomEvent } from 'livekit-client';
import { VoiceParticipant, UserData } from '../types';
import { MicIcon, VolumeOffIcon, CloseIcon, CameraIcon, ScreenShareIcon, FlipCameraIcon } from './Icons';
import { requestMicrophonePermission, checkSecureContext } from '../utils/permissionUtils';
import { API_URL } from '../api';

export interface RemoteStreamViewProps {
    stream: MediaStream | null;
    isLocal?: boolean;
    muted?: boolean;
    className?: string;
    style?: React.CSSProperties;
    onAspectRatioChange?: (ratio: number) => void;
}

export const RemoteStreamView = React.memo(({ stream, isLocal, muted, className, style, onAspectRatioChange }: RemoteStreamViewProps) => {
    const videoRef = useRef<HTMLVideoElement>(null);

    useEffect(() => {
        const video = videoRef.current;
        if (video && stream) {
            if (video.srcObject !== stream) {
                video.srcObject = stream;
            }
            video.play().catch(err => {
                console.warn('[RemoteStreamView] play() failed/prevented:', err);
            });

            const track = stream.getVideoTracks()[0];
            if (track) {
                const settings = track.getSettings();
                if (settings?.width && settings?.height && onAspectRatioChange) {
                    onAspectRatioChange(settings.width / settings.height);
                }
            }
        }
        return () => {
            if (video) {
                video.srcObject = null;
            }
        };
    }, [stream, onAspectRatioChange]);

    const handleLoadedMetadata = (e: React.SyntheticEvent<HTMLVideoElement>) => {
        const video = e.currentTarget;
        if (video.videoWidth && video.videoHeight && onAspectRatioChange) {
            onAspectRatioChange(video.videoWidth / video.videoHeight);
        }
    };

    return (
        <video
            ref={videoRef}
            autoPlay
            playsInline
            muted={muted !== undefined ? muted : true}
            onLoadedMetadata={handleLoadedMetadata}
            className={className}
            style={style}
        />
    );
});

export const RemoteAudioPlayer = React.memo(({ stream, muted }: { stream: MediaStream | null; muted?: boolean }) => {
    const audioRef = useRef<HTMLAudioElement>(null);

    useEffect(() => {
        const audio = audioRef.current;
        if (!audio || !stream) return;

        const attemptPlay = () => {
            if (audio.srcObject !== stream) {
                audio.srcObject = stream;
            }
            audio.play().catch(err => {
                console.warn('[RemoteAudioPlayer] play() failed/prevented:', err);
            });
        };

        attemptPlay();

        // Listen for new tracks added dynamically to the stream
        stream.addEventListener('addtrack', attemptPlay);
        stream.addEventListener('removetrack', attemptPlay);

        // Auto unlock audio on user gesture if browser autoplay policy blocked initial attempt
        const unlockAudio = () => {
            if (audio.paused) {
                attemptPlay();
            }
        };

        window.addEventListener('click', unlockAudio);
        window.addEventListener('touchstart', unlockAudio);
        window.addEventListener('keydown', unlockAudio);

        return () => {
            stream.removeEventListener('addtrack', attemptPlay);
            stream.removeEventListener('removetrack', attemptPlay);
            window.removeEventListener('click', unlockAudio);
            window.removeEventListener('touchstart', unlockAudio);
            window.removeEventListener('keydown', unlockAudio);
            if (audio) {
                audio.srcObject = null;
            }
        };
    }, [stream]);

    return (
        <audio
            ref={audioRef}
            autoPlay
            playsInline
            muted={muted}
            style={{ display: 'none' }}
        />
    );
});

interface VoiceRoomProps {
    communityId: string;
    channelId: string;
    currentUser: string;
    usersData: Record<string, UserData>;
    socket: any;
    themeColor?: string;
    onLeave?: () => void;
    onRemoteStreamsUpdate?: (streams: Record<string, MediaStream>) => void;
    onLocalStreamUpdate?: (stream: MediaStream | null) => void;
    isMuted?: boolean;
    setIsMuted?: (muted: boolean) => void;
    isDeafened?: boolean;
    setIsDeafened?: (deafened: boolean) => void;
}

export const VoiceRoom: React.FC<VoiceRoomProps> = ({ 
    communityId, channelId, currentUser, usersData, socket, 
    themeColor = '#2563eb', onLeave, onRemoteStreamsUpdate, onLocalStreamUpdate,
    isMuted: propIsMuted, setIsMuted: propSetIsMuted,
    isDeafened: propIsDeafened, setIsDeafened: propSetIsDeafened
}) => {
    const [participants, setParticipants] = useState<VoiceParticipant[]>([]);
    const [localStream, setLocalStream] = useState<MediaStream | null>(null);
    const [localScreenStream, setLocalScreenStream] = useState<MediaStream | null>(null);
    const [localMuted, setLocalMuted] = useState(false);
    const [localDeafened, setLocalDeafened] = useState(false);
    const [isCameraOn, setIsCameraOn] = useState(false);
    const [isScreenSharing, setIsScreenSharing] = useState(false);
    const [facingMode, setFacingMode] = useState<'user' | 'environment'>('user');
    const [focusedParticipant, setFocusedParticipant] = useState<{ username: string; isScreenShare: boolean } | null>(null);
    const isMobile = Capacitor.isNativePlatform() || /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

    const isMuted = propIsMuted !== undefined ? propIsMuted : localMuted;
    const setIsMuted = propSetIsMuted !== undefined ? propSetIsMuted : setLocalMuted;
    const isDeafened = propIsDeafened !== undefined ? propIsDeafened : localDeafened;
    const setIsDeafened = propSetIsDeafened !== undefined ? propSetIsDeafened : setLocalDeafened;

    const roomRef = useRef<Room | null>(null);
    const [remoteStreamsMap, setRemoteStreamsMap] = useState<Record<string, MediaStream>>({});
    const streamsRef = useRef<Record<string, MediaStream>>({});
    const [micError, setMicError] = useState<string | null>(null);

    const isMutedRef = useRef(isMuted);
    const isDeafenedRef = useRef(isDeafened);
    const isCameraOnRef = useRef(isCameraOn);
    const isScreenSharingRef = useRef(isScreenSharing);
    const isSpeakingRef = useRef(false);
    const participantsRef = useRef(participants);

    const [logs, setLogs] = useState<string[]>([]);
    const addLog = (msg: string) => {
        const timestamp = new Date().toLocaleTimeString();
        console.log(`[VoiceDiagnostics] ${msg}`);
        setLogs(prev => [`[${timestamp}] ${msg}`, ...prev].slice(0, 30));
    };

    useEffect(() => { isMutedRef.current = isMuted; }, [isMuted]);
    useEffect(() => { isDeafenedRef.current = isDeafened; }, [isDeafened]);
    useEffect(() => { isCameraOnRef.current = isCameraOn; }, [isCameraOn]);
    useEffect(() => { isScreenSharingRef.current = isScreenSharing; }, [isScreenSharing]);

    useEffect(() => {
        if (onLocalStreamUpdate) onLocalStreamUpdate(localStream);
    }, [localStream, onLocalStreamUpdate]);

    useEffect(() => {
        if (onRemoteStreamsUpdate) onRemoteStreamsUpdate(remoteStreamsMap);
    }, [remoteStreamsMap, onRemoteStreamsUpdate]);

    const effectiveMuted = isMuted || isDeafened;


    const initLiveKit = async () => {
        setMicError(null);
        let room: Room | null = null;
        try {
            // 1. Request microphone permissions
            try {
                addLog('Requesting microphone permission...');
                await requestMicrophonePermission();
                addLog('Microphone permission step completed.');
            } catch (err: any) {
                addLog(`Mic permission error: ${err.name} - ${err.message}`);
                setMicError('Microphone permission denied or hardware unavailable.');
                return;
            }

            // 2. Fetch LiveKit Token
            let data: any;
            try {
                const roomName = `${communityId}:${channelId}`;
                addLog(`Fetching LiveKit token for room: ${roomName}`);
                const res = await fetch(`${API_URL}livekit/token?room=${encodeURIComponent(roomName)}&identity=${encodeURIComponent(currentUser)}`);
                if (!res.ok) throw new Error(`HTTP ${res.status}`);
                data = await res.json();
                addLog('LiveKit token fetched successfully.');
            } catch (err: any) {
                addLog(`Token fetch error: ${err.message}`);
                setMicError(`Failed to generate LiveKit room token (${err.message}).`);
                return;
            }

            // 3. Setup Room & Connect
            try {
                room = new Room({
                    adaptiveStream: true,
                    dynacast: true,
                    autoSubscribe: true,
                    audioCaptureDefaults: {
                        autoGainControl: true,
                        echoCancellation: true,
                        noiseSuppression: true
                    }
                });
                roomRef.current = room;

                const updateLocalStream = () => {
                    const primaryTracks = Array.from(room!.localParticipant.trackPublications.values())
                        .filter(pub => pub.source !== 'screen_share')
                        .map(pub => pub.track)
                        .filter(t => t && t.mediaStreamTrack);
                        
                    const screenTracks = Array.from(room!.localParticipant.trackPublications.values())
                        .filter(pub => pub.source === 'screen_share')
                        .map(pub => pub.track)
                        .filter(t => t && t.mediaStreamTrack);
                        
                    if (primaryTracks.length > 0) {
                        const stream = new MediaStream();
                        primaryTracks.forEach(t => { if (t?.mediaStreamTrack) stream.addTrack(t.mediaStreamTrack); });
                        setLocalStream(stream);
                    } else {
                        setLocalStream(null);
                    }

                    if (screenTracks.length > 0) {
                        const stream = new MediaStream();
                        screenTracks.forEach(t => { if (t?.mediaStreamTrack) stream.addTrack(t.mediaStreamTrack); });
                        setLocalScreenStream(stream);
                    } else {
                        setLocalScreenStream(null);
                    }
                };

                const updateRemoteStreams = () => {
                    const newMap: Record<string, MediaStream> = { ...streamsRef.current };
                    
                    room!.remoteParticipants.forEach((participant) => {
                        const usernameKey = participant.identity.toLowerCase();
                        
                        const primaryTracks = Array.from(participant.trackPublications.values())
                            .filter(pub => pub.source !== 'screen_share')
                            .map(pub => pub.track || pub.videoTrack || pub.audioTrack)
                            .filter((t): t is any => !!(t && t.mediaStreamTrack));
                            
                        const screenTracks = Array.from(participant.trackPublications.values())
                            .filter(pub => pub.source === 'screen_share')
                            .map(pub => pub.track || pub.videoTrack || pub.audioTrack)
                            .filter((t): t is any => !!(t && t.mediaStreamTrack));

                        // 1. Camera / Mic Stream
                        if (primaryTracks.length > 0) {
                            let stream = streamsRef.current[usernameKey];
                            if (!stream) {
                                stream = new MediaStream();
                                streamsRef.current[usernameKey] = stream;
                            }
                            
                            const currentTracks = stream.getTracks();
                            const targetTracks = primaryTracks.map(t => t.mediaStreamTrack);

                            currentTracks.forEach(ct => { if (!targetTracks.includes(ct)) stream.removeTrack(ct); });
                            targetTracks.forEach(tt => { if (!currentTracks.includes(tt)) stream.addTrack(tt); });

                            newMap[usernameKey] = stream;
                        } else {
                            if (newMap[usernameKey]) {
                                delete newMap[usernameKey];
                                delete streamsRef.current[usernameKey];
                            }
                        }

                        // 2. Screen-share Stream
                        const screenKey = `${usernameKey}-screen`;
                        if (screenTracks.length > 0) {
                            let stream = streamsRef.current[screenKey];
                            if (!stream) {
                                stream = new MediaStream();
                                streamsRef.current[screenKey] = stream;
                            }
                            
                            const currentTracks = stream.getTracks();
                            const targetTracks = screenTracks.map(t => t.mediaStreamTrack);

                            currentTracks.forEach(ct => { if (!targetTracks.includes(ct)) stream.removeTrack(ct); });
                            targetTracks.forEach(tt => { if (!currentTracks.includes(tt)) stream.addTrack(tt); });

                            newMap[screenKey] = stream;
                        } else {
                            if (newMap[screenKey]) {
                                delete newMap[screenKey];
                                delete streamsRef.current[screenKey];
                            }
                        }
                    });

                    const activeKeys = Array.from(room!.remoteParticipants.values()).map(p => p.identity.toLowerCase());
                    Object.keys(newMap).forEach(key => {
                        const baseKey = key.endsWith('-screen') ? key.replace('-screen', '') : key;
                        if (!activeKeys.includes(baseKey)) {
                            delete newMap[key];
                            delete streamsRef.current[key];
                        }
                    });

                    setRemoteStreamsMap({ ...newMap });
                };

                room.on(RoomEvent.LocalTrackPublished, updateLocalStream);
                room.on(RoomEvent.LocalTrackUnpublished, updateLocalStream);
                room.on(RoomEvent.AudioPlaybackStatusChanged, () => {
                    addLog(`AudioPlaybackStatusChanged: canPlaybackAudio=${room?.canPlaybackAudio}`);
                    if (room && !room.canPlaybackAudio) {
                        room.startAudio().catch(err => console.warn('[VoiceRoom] startAudio failed:', err));
                    }
                });
                room.on(RoomEvent.TrackSubscribed, (track, pub, participant) => {
                    addLog(`Subscribed to track ${track.sid} (${track.kind}) from ${participant.identity}`);
                    if (track.kind === 'audio' && room) {
                        room.startAudio().catch(() => {});
                    }
                    updateRemoteStreams();
                });
                room.on(RoomEvent.TrackUnsubscribed, (track, pub, participant) => {
                    addLog(`Unsubscribed from track ${track.sid} (${track.kind}) from ${participant.identity}`);
                    updateRemoteStreams();
                });
                room.on(RoomEvent.ParticipantConnected, (p) => {
                    addLog(`Remote participant joined LiveKit: ${p.identity}`);
                    updateRemoteStreams();
                });
                room.on(RoomEvent.ParticipantDisconnected, (p) => {
                    addLog(`Remote participant left LiveKit: ${p.identity}`);
                    updateRemoteStreams();
                });

                room.on(RoomEvent.ActiveSpeakersChanged, (speakers) => {
                    const speakingMap: Record<string, boolean> = {};
                    speakers.forEach(s => {
                        speakingMap[s.identity] = true;
                    });
                    
                    setParticipants(prev => prev.map(p => ({
                        ...p,
                        isSpeaking: !!speakingMap[p.username]
                    })));

                    const isLocalSpeaking = !!speakingMap[currentUser];
                    if (isLocalSpeaking !== isSpeakingRef.current) {
                        isSpeakingRef.current = isLocalSpeaking;
                        socket.emit('voice-state-update', { isSpeaking: isLocalSpeaking });
                    }
                });

                addLog(`Connecting to LiveKit room...`);
                await room.connect(data.url, data.token);
                addLog(`Connected successfully to room: ${room.name}`);
                await room.startAudio().catch(err => addLog(`startAudio error: ${err.message}`));
                updateRemoteStreams();

                // Staggered auto-sync timers so existing tracks that settle after connect are bound automatically
                setTimeout(() => updateRemoteStreams(), 500);
                setTimeout(() => updateRemoteStreams(), 1500);
                setTimeout(() => updateRemoteStreams(), 3000);
            } catch (err: any) {
                addLog(`LiveKit connect error: ${err.name} - ${err.message}`);
                setMicError(`Could not connect to LiveKit server. Please check network/server logs.`);
                return;
            }

            // 4. Publish Microphone Track
            try {
                addLog(`Publishing audio track (muted: ${effectiveMuted})`);
                await room.localParticipant.setMicrophoneEnabled(!effectiveMuted);
                addLog(`Audio track successfully published.`);
            } catch (err: any) {
                addLog(`Mic publish error: ${err.name} - ${err.message}`);
                setMicError(`Microphone access blocked. Please check browser permissions or secure context.`);
            }

        } catch (err: any) {
            addLog(`Unexpected voice setup error: ${err.message}`);
            setMicError(`Unexpected error during setup (${err.message})`);
        }
    };

    useEffect(() => {
        // Emit join immediately so presence list is populated right away
        socket.emit('join-voice-room', { communityId, channelId, username: currentUser });

        initLiveKit();

        socket.on('voice-participants-list', (list: VoiceParticipant[]) => {
            addLog(`Received voice-participants-list. Count: ${list.length}`);
            setParticipants(list);
            socket.emit('voice-state-update', {
                isMuted: isMutedRef.current || isDeafenedRef.current,
                isDeafened: isDeafenedRef.current,
                isCameraOn: isCameraOnRef.current,
                isScreenSharing: isScreenSharingRef.current
            });
        });

        socket.on('user-joined-voice', ({ username, socketId }: { username: string, socketId: string }) => {
            addLog(`Socket User joined voice: ${username}`);
            setParticipants(prev => {
                const existing = prev.find(p => p.username === username);
                if (existing) {
                    return prev.map(p => p.username === username ? { ...p, socketId, isMuted: false, isDeafened: false, isSpeaking: false } : p);
                }
                return [...prev, { username, socketId, isMuted: false, isDeafened: false, isSpeaking: false }];
            });
        });

        socket.on('user-left-voice', ({ username, socketId }: { username: string, socketId: string }) => {
            addLog(`Socket User left voice: ${username}`);
            setParticipants(prev => prev.filter(p => p.socketId !== socketId));
        });

        socket.on('voice-state-update', ({ socketId, state }: { socketId: string, state: any }) => {
            setParticipants(prev => prev.map(p => p.socketId === socketId ? { ...p, ...state } : p));
        });

        // Track visibility to log & handle background drops
        const handleVisibilityChange = () => {
            if (document.hidden) {
                addLog('App entered BACKGROUND.');
            } else {
                addLog('App entered FOREGROUND.');
                if (roomRef.current) {
                    addLog(`Connection state is: ${roomRef.current.state}`);
                    if (roomRef.current.state === 'disconnected') {
                        addLog('Connection lost. Reconnecting...');
                        try {
                            roomRef.current.disconnect();
                        } catch (e) {}
                        roomRef.current = null;
                        initLiveKit();
                    }
                }
            }
        };
        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            addLog('Leaving voice room and cleaning up...');
            socket.emit('leave-voice-room');
            socket.off('voice-participants-list');
            socket.off('user-joined-voice');
            socket.off('user-left-voice');
            socket.off('voice-state-update');
            document.removeEventListener('visibilitychange', handleVisibilityChange);

            if (roomRef.current) {
                roomRef.current.disconnect();
                roomRef.current = null;
            }
        };
    }, [communityId, channelId, currentUser]);

    // Acquire and manage Screen Wake Lock to prevent screen sleep / JS pausing on Android
    useEffect(() => {
        let wakeLock: any = null;
        const requestWakeLock = async () => {
            try {
                if ('wakeLock' in navigator) {
                    wakeLock = await (navigator as any).wakeLock.request('screen');
                    addLog('Screen Wake Lock acquired.');
                }
            } catch (err: any) {
                addLog(`Wake Lock acquisition failed: ${err.message}`);
            }
        };
        requestWakeLock();

        const handleWakeLockVisibility = () => {
            if (document.visibilityState === 'visible' && !wakeLock) {
                requestWakeLock();
            }
        };
        document.addEventListener('visibilitychange', handleWakeLockVisibility);

        return () => {
            document.removeEventListener('visibilitychange', handleWakeLockVisibility);
            if (wakeLock) {
                wakeLock.release().then(() => {
                    addLog('Screen Wake Lock released.');
                }).catch((e: any) => {
                    console.error('Error releasing wake lock:', e);
                });
            }
        };
    }, []);

    // Update microphone mute status dynamically
    useEffect(() => {
        if (roomRef.current && roomRef.current.state === 'connected') {
            addLog(`Updating microphone mute status to: ${effectiveMuted}`);
            roomRef.current.localParticipant.setMicrophoneEnabled(!effectiveMuted).catch(err => {
                addLog(`Failed to toggle microphone: ${err.message}`);
            });
        }
        socket.emit('voice-state-update', { isMuted: effectiveMuted, isDeafened: isDeafened });
        setParticipants(prev => prev.map(p => p.username === currentUser ? { ...p, isMuted: effectiveMuted, isDeafened: isDeafened } : p));
    }, [effectiveMuted, isDeafened, currentUser]);

    const toggleCamera = async () => {
        try {
            const targetState = !isCameraOn;
            addLog(`Toggling camera to: ${targetState}`);
            if (roomRef.current && roomRef.current.state === 'connected') {
                await roomRef.current.localParticipant.setCameraEnabled(targetState, {
                    facingMode: facingMode
                });
                
                // Get local stream from LiveKit local participant tracks
                const tracks = Array.from(roomRef.current.localParticipant.trackPublications.values())
                    .map(pub => pub.track)
                    .filter(t => t && t.mediaStreamTrack);
                if (tracks.length > 0) {
                    const stream = new MediaStream();
                    tracks.forEach(t => {
                        if (t?.mediaStreamTrack) stream.addTrack(t.mediaStreamTrack);
                    });
                    setLocalStream(stream);
                } else {
                    setLocalStream(null);
                }
            } else {
                console.warn('[LiveKit] Room not connected. Toggling camera in local preview mode.');
                if (targetState) {
                    const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode } });
                    const videoTrack = stream.getVideoTracks()[0];
                    let nextStream = localStream;
                    if (!nextStream) {
                        nextStream = new MediaStream();
                    } else {
                        nextStream.getVideoTracks().forEach(t => { t.stop(); nextStream?.removeTrack(t); });
                    }
                    nextStream.addTrack(videoTrack);
                    setLocalStream(new MediaStream(nextStream.getTracks()));
                } else {
                    if (localStream) {
                        localStream.getVideoTracks().forEach(t => { t.stop(); localStream.removeTrack(t); });
                        setLocalStream(new MediaStream(localStream.getTracks()));
                    }
                }
            }
            setIsCameraOn(targetState);
            setIsScreenSharing(false);
            socket.emit('voice-state-update', { isCameraOn: targetState, isScreenSharing: false });
        } catch (err: any) {
            addLog(`Camera error: ${err.message}`);
            console.error('[LiveKit] Camera toggle error:', err);
        }
    };

    // Update camera facingMode
    useEffect(() => {
        const updateFacing = async () => {
            if (roomRef.current && roomRef.current.state === 'connected' && roomRef.current.localParticipant.isCameraEnabled) {
                try {
                    addLog(`Flipping camera to: ${facingMode}`);
                    await roomRef.current.localParticipant.setCameraEnabled(false);
                    
                    // Enumerate video devices to pick the correct one
                    const devices = await navigator.mediaDevices.enumerateDevices();
                    const videoDevices = devices.filter(d => d.kind === 'videoinput');
                    
                    let targetDevice: MediaDeviceInfo | undefined;
                    if (facingMode === 'environment') {
                        // Look for environment/back camera explicitly
                        targetDevice = videoDevices.find(d => 
                            d.label.toLowerCase().includes('back') || 
                            d.label.toLowerCase().includes('rear') || 
                            d.label.toLowerCase().includes('environment') ||
                            d.label.toLowerCase().includes('facing back') ||
                            d.label.toLowerCase().includes('0, back')
                        );
                        if (!targetDevice && videoDevices.length > 1) {
                            // Fallback: usually index 1 is environment/back camera on phones
                            targetDevice = videoDevices[1];
                        }
                    } else {
                        // Look for front/user camera explicitly
                        targetDevice = videoDevices.find(d => 
                            d.label.toLowerCase().includes('front') || 
                            d.label.toLowerCase().includes('user') ||
                            d.label.toLowerCase().includes('facing front') ||
                            d.label.toLowerCase().includes('1, front')
                        );
                        if (!targetDevice && videoDevices.length > 0) {
                            targetDevice = videoDevices[0];
                        }
                    }
                    
                    if (targetDevice) {
                        addLog(`Selecting device: ${targetDevice.label || targetDevice.deviceId}`);
                        await roomRef.current.localParticipant.setCameraEnabled(true, {
                            deviceId: targetDevice.deviceId
                        });
                    } else {
                        // Fallback to constraints
                        await roomRef.current.localParticipant.setCameraEnabled(true, {
                            facingMode: facingMode
                        });
                    }
                    addLog(`Camera flipped successfully.`);
                } catch (err: any) {
                    addLog(`Camera flip error: ${err.message}`);
                }
                
                // Get local stream from LiveKit local participant tracks
                const tracks = Array.from(roomRef.current.localParticipant.trackPublications.values())
                    .map(pub => pub.track)
                    .filter(t => t && t.mediaStreamTrack);
                if (tracks.length > 0) {
                    const stream = new MediaStream();
                    tracks.forEach(t => {
                        if (t?.mediaStreamTrack) stream.addTrack(t.mediaStreamTrack);
                    });
                    setLocalStream(stream);
                }
            } else if (isCameraOn) {
                try {
                    // Enumerate video devices to pick the correct one
                    const devices = await navigator.mediaDevices.enumerateDevices();
                    const videoDevices = devices.filter(d => d.kind === 'videoinput');
                    
                    let targetDevice: MediaDeviceInfo | undefined;
                    if (facingMode === 'environment') {
                        targetDevice = videoDevices.find(d => 
                            d.label.toLowerCase().includes('back') || 
                            d.label.toLowerCase().includes('rear') || 
                            d.label.toLowerCase().includes('environment')
                        );
                        if (!targetDevice && videoDevices.length > 1) {
                            targetDevice = videoDevices[1];
                        }
                    } else {
                        targetDevice = videoDevices.find(d => 
                            d.label.toLowerCase().includes('front') || 
                            d.label.toLowerCase().includes('user')
                        );
                        if (!targetDevice && videoDevices.length > 0) {
                            targetDevice = videoDevices[0];
                        }
                    }
                    
                    const constraints: MediaStreamConstraints = targetDevice 
                        ? { video: { deviceId: { exact: targetDevice.deviceId } } }
                        : { video: { facingMode } };
                        
                    const stream = await navigator.mediaDevices.getUserMedia(constraints);
                    const videoTrack = stream.getVideoTracks()[0];
                    let nextStream = localStream;
                    if (!nextStream) {
                        nextStream = new MediaStream();
                    } else {
                        nextStream.getVideoTracks().forEach(t => { t.stop(); nextStream?.removeTrack(t); });
                    }
                    nextStream.addTrack(videoTrack);
                    setLocalStream(new MediaStream(nextStream.getTracks()));
                } catch (err) {
                    console.error('[LiveKit] Fallback camera flip error:', err);
                }
            }
        };
        updateFacing();
    }, [facingMode]);

    const toggleScreenShare = async () => {
        try {
            const targetState = !isScreenSharing;
            addLog(`Toggling screen sharing to: ${targetState}`);
            if (roomRef.current && roomRef.current.state === 'connected') {
                const isMobile = typeof window !== 'undefined' && window.matchMedia('(pointer: coarse)').matches;
                await roomRef.current.localParticipant.setScreenShareEnabled(targetState, {
                    audio: true,
                    resolution: isMobile ? { width: 1080, height: 1920, frameRate: 30 } : undefined
                }, {
                    simulcast: true,
                    videoCodec: 'vp8',
                    videoEncoding: {
                        maxBitrate: 3000000,
                        maxFramerate: 30
                    }
                });
                
                const tracks = Array.from(roomRef.current.localParticipant.trackPublications.values())
                    .map(pub => pub.track)
                    .filter(t => t && t.mediaStreamTrack);
                if (tracks.length > 0) {
                    const stream = new MediaStream();
                    tracks.forEach(t => {
                        if (t?.mediaStreamTrack) stream.addTrack(t.mediaStreamTrack);
                    });
                    setLocalStream(stream);
                } else {
                    setLocalStream(null);
                }
            } else {
                console.warn('[LiveKit] Room not connected. Toggling screen share in local preview mode.');
                if (targetState) {
                    const isMobile = typeof window !== 'undefined' && window.matchMedia('(pointer: coarse)').matches;
                    const mediaConstraints: any = isMobile
                        ? { video: { width: { ideal: 1080 }, height: { ideal: 1920 }, frameRate: { max: 30 } }, audio: true }
                        : { video: true, audio: true };

                    const stream = await (navigator.mediaDevices as any).getDisplayMedia(mediaConstraints);
                    const screenTrack = stream.getVideoTracks()[0];
                    let nextStream = localStream;
                    if (!nextStream) {
                        nextStream = new MediaStream();
                    } else {
                        nextStream.getVideoTracks().forEach(t => { t.stop(); nextStream?.removeTrack(t); });
                    }
                    nextStream.addTrack(screenTrack);
                    setLocalStream(new MediaStream(nextStream.getTracks()));
                    
                    screenTrack.onended = () => {
                        setIsScreenSharing(false);
                        socket.emit('voice-state-update', { isScreenSharing: false });
                        if (localStream) {
                            localStream.getVideoTracks().forEach(t => { t.stop(); localStream.removeTrack(t); });
                            setLocalStream(new MediaStream(localStream.getTracks()));
                        }
                    };
                } else {
                    if (localStream) {
                        localStream.getVideoTracks().forEach(t => { t.stop(); localStream.removeTrack(t); });
                        setLocalStream(new MediaStream(localStream.getTracks()));
                    }
                }
            }
            setIsScreenSharing(targetState);
            setIsCameraOn(false);
            socket.emit('voice-state-update', { isScreenSharing: targetState, isCameraOn: false });
        } catch (err: any) {
            addLog(`Screen share error: ${err.message}`);
            console.error('[LiveKit] Screen share toggle error:', err);
        }
    };

    const renderedParticipants = useMemo(() => {
        const screenshares: Array<{
            id: string;
            p: VoiceParticipant;
            isScreenShareItem: boolean;
        }> = [];
        const regulars: Array<{
            id: string;
            p: VoiceParticipant;
            isScreenShareItem: boolean;
        }> = [];
        
        participants.forEach(p => {
            // Push screenshare window first so it is appended to the front!
            if (p.isScreenSharing) {
                screenshares.push({
                    id: `${p.socketId}-screen`,
                    p: p,
                    isScreenShareItem: true
                });
            }
            // Always push primary user presence
            regulars.push({
                id: `${p.socketId}-primary`,
                p: p,
                isScreenShareItem: false
            });
        });
        
        // Appended to the front!
        return [...screenshares, ...regulars];
    }, [participants]);

    const focusedOverlayNode = focusedParticipant && (
        <div 
            className="fixed inset-0 z-[150] bg-black/95 backdrop-blur-md flex flex-col items-center justify-center p-4 animate-fade-in"
            onClick={() => setFocusedParticipant(null)}
        >
            <div className="absolute top-4 right-4 z-[160]">
                <button 
                    type="button" 
                    onClick={() => setFocusedParticipant(null)}
                    className="p-3 bg-white/10 hover:bg-white/20 text-white rounded-full transition-all active:scale-95 shadow-lg"
                >
                    <CloseIcon className="w-6 h-6" />
                </button>
            </div>
            
            <div className={`w-full h-full flex items-center justify-center ${focusedParticipant.isScreenShare ? 'max-w-full max-h-full' : 'max-w-2xl max-h-[70vh] rounded-[2rem] overflow-hidden border border-white/10 shadow-2xl bg-black/50'}`}>
                {(() => {
                    const isLocal = focusedParticipant.username === currentUser;
                    const participantStream = focusedParticipant.isScreenShare
                        ? (isLocal ? localScreenStream : remoteStreamsMap[`${focusedParticipant.username.toLowerCase()}-screen`])
                        : (isLocal ? localStream : remoteStreamsMap[focusedParticipant.username.toLowerCase()]);
                    return (
                        <RemoteStreamView 
                            stream={participantStream}
                            className="w-full h-full object-contain"
                        />
                    );
                })()}
            </div>
            
            <div className="absolute bottom-6 px-4 py-2 rounded-full bg-black/60 border border-white/10 backdrop-blur-sm text-white text-sm font-bold shadow-lg">
                {focusedParticipant.isScreenShare ? `📺 Screen Share: ${focusedParticipant.username}` : `📷 Camera: ${focusedParticipant.username}`}
            </div>
        </div>
    );

    const participantsDest = document.getElementById('voice-participants-destination');
    const controlsDest = document.getElementById('voice-controls-destination');

    const participantsNode = (
        <div className="flex items-center gap-2 overflow-x-auto p-2 scrollbar-hide">
            {renderedParticipants.map(item => {
                const p = item.p;
                const userData = usersData[p.username] || { displayName: p.username, pfp: '' };
                const activeThemeColor = (userData as any).appThemeColor || themeColor;
                
                const isLocal = p.username === currentUser;
                const participantStream = item.isScreenShareItem
                    ? (isLocal ? localScreenStream : remoteStreamsMap[`${p.username.toLowerCase()}-screen`])
                    : (isLocal ? localStream : remoteStreamsMap[p.username.toLowerCase()]);
                    
                const hasVideo = item.isScreenShareItem
                    ? !!(participantStream && participantStream.getVideoTracks().length > 0)
                    : !!(p.isCameraOn || (participantStream && participantStream.getVideoTracks().length > 0));

                return (
                    <div key={item.id} className="relative flex-shrink-0">
                        <ParticipantCard
                            p={p}
                            userData={userData}
                            activeThemeColor={activeThemeColor}
                            hasVideo={hasVideo}
                            participantStream={participantStream}
                            currentUser={currentUser}
                            isDeafened={!!p.isDeafened}
                            isScreenShareItem={item.isScreenShareItem}
                            small={true}
                            onClick={() => setFocusedParticipant({ username: p.username, isScreenShare: item.isScreenShareItem })}
                        />
                    </div>
                );
            })}
        </div>
    );

    const mainAreaNode = (
        <div className="flex-1 overflow-y-auto p-6">
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 xl:grid-cols-8 gap-x-4 gap-y-10">
                {renderedParticipants.map(item => {
                    const p = item.p;
                    const userData = usersData[p.username] || { displayName: p.username, pfp: '' };
                    const activeThemeColor = (userData as any).appThemeColor || themeColor;
                    
                    const isLocal = p.username === currentUser;
                    const participantStream = item.isScreenShareItem
                        ? (isLocal ? localScreenStream : remoteStreamsMap[`${p.username.toLowerCase()}-screen`])
                        : (isLocal ? localStream : remoteStreamsMap[p.username.toLowerCase()]);
                        
                    const hasVideo = item.isScreenShareItem
                        ? !!(participantStream && participantStream.getVideoTracks().length > 0)
                        : !!(p.isCameraOn || (participantStream && participantStream.getVideoTracks().length > 0));

                    return (
                        <ParticipantCard
                            key={item.id}
                            p={p}
                            userData={userData}
                            activeThemeColor={activeThemeColor}
                            hasVideo={hasVideo}
                            participantStream={participantStream}
                            currentUser={currentUser}
                            isDeafened={!!p.isDeafened}
                            isScreenShareItem={item.isScreenShareItem}
                            onClick={() => setFocusedParticipant({ username: p.username, isScreenShare: item.isScreenShareItem })}
                        />
                    );
                })}
            </div>
        </div>
    );

    const controlsNode = (
        <div className="w-full overflow-x-hidden">
            <div className="max-w-2xl mx-auto flex flex-wrap items-center justify-center gap-3 sm:gap-6">
                <button
                    onClick={() => setIsMuted(!isMuted)}
                    className={`p-4 text-raised rounded-full transition-all duration-300 ${isMuted ? 'bg-red-500 text-white' : 'bg-white/5 text-gray-300 hover:bg-white/10'}`}
                    title={isMuted ? "Unmute Mic" : "Mute Mic"}
                >
                    {isMuted ? <VolumeOffIcon className="w-6 h-6" /> : <MicIcon className="w-6 h-6" />}
                </button>

                <button
                    onClick={() => {
                        const nextDeafen = !isDeafened;
                        setIsDeafened(nextDeafen);
                        if (nextDeafen) {
                            setIsMuted(true);
                        }
                    }}
                    className={`p-4 text-raised rounded-full transition-all duration-300 ${isDeafened ? 'bg-red-500 text-white' : 'bg-white/5 text-gray-300 hover:bg-white/10'}`}
                    title={isDeafened ? "Undeafen Audio" : "Deafen Audio"}
                >
                    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 18v-6a9 9 0 0118 0v6M4 19a2 2 0 002-2v-3a2 2 0 00-2-2H3v7h1zm16 0h1v-7h-1a2 2 0 00-2 2v3a2 2 0 002 2z" />
                        {isDeafened && <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3l18 18" />}
                    </svg>
                </button>

                <button
                    onClick={toggleCamera}
                    className={`p-4 text-raised rounded-full transition-all duration-300 ${isCameraOn ? 'bg-blue-500 text-white' : 'bg-white/5 text-gray-300 hover:bg-white/10'}`}
                >
                    <CameraIcon className="w-6 h-6" />
                </button>

                {isCameraOn && isMobile && (
                    <button
                        onClick={() => setFacingMode(prev => prev === 'user' ? 'environment' : 'user')}
                        className="p-4 text-raised bg-white/5 text-gray-300 hover:bg-white/10 rounded-full transition-all duration-300"
                    >
                        <FlipCameraIcon className="w-6 h-6" />
                    </button>
                )}

                <button
                    onClick={toggleScreenShare}
                    className={`p-4 rounded-full transition-all duration-300 ${isScreenSharing ? 'bg-green-500 text-white' : 'bg-white/5 text-gray-300 hover:bg-white/10'}`}
                >
                    <ScreenShareIcon className="w-6 h-6" />
                </button>

                <button
                    onClick={() => onLeave ? onLeave() : window.location.reload()}
                    className="p-4 text-raised bg-red-500 hover:bg-red-600 text-white rounded-full transition-all duration-300"
                >
                    <CloseIcon className="w-6 h-6" />
                </button>
            </div>
        </div>
    );

    const diagnosticsNode = (
        <DiagnosticsPanel
            logs={logs}
            room={roomRef.current}
            participants={participants}
            remoteStreamsMap={remoteStreamsMap}
            currentUser={currentUser}
            micError={micError}
            isMuted={isMuted}
            isDeafened={isDeafened}
            onReconnect={() => {
                addLog('User requested manual reconnect.');
                if (roomRef.current) {
                    roomRef.current.disconnect();
                    roomRef.current = null;
                }
                initLiveKit();
            }}
        />
    );

    return (
        <div className="flex flex-col h-full rounded-2xl m-4 overflow-hidden">
            {/* Visual Mic Error Banner at top of room */}
            {micError && (
                <div className="mx-4 mt-4 p-4 bg-red-500/20 border border-red-500/30 rounded-2xl text-red-200 text-sm font-medium animate-fade-in flex flex-col gap-1.5">
                    <span className="font-bold">⚠️ Microphone / Connection Error</span>
                    <span>{micError}</span>
                </div>
            )}

            {participantsDest ? createPortal(participantsNode, participantsDest) : mainAreaNode}
            <div className=" p-6">
                {controlsDest ? createPortal(controlsNode, controlsDest) : controlsNode}
            </div>
            {focusedOverlayNode}

            {/* Hidden persistent audio elements at root level */}
            <div style={{ display: 'none' }}>
                {participants.map(p => {
                    if (p.username === currentUser) return null;
                    const participantStream = remoteStreamsMap[p.username.toLowerCase()];
                    if (!participantStream) return null;
                    return (
                        <RemoteAudioPlayer 
                            key={`audio-${p.username}`} 
                            stream={participantStream} 
                            muted={isDeafened} 
                        />
                    );
                })}
            </div>

            {/* Floating Diagnostics widget */}
            {diagnosticsNode}
        </div>
    );
};

const ParticipantCard = React.memo(({ p, userData, activeThemeColor, hasVideo, participantStream, currentUser, isDeafened, small, isScreenShareItem, onClick }: {
    p: VoiceParticipant;
    userData: any;
    activeThemeColor: string;
    hasVideo: boolean;
    participantStream: MediaStream | null;
    currentUser: string;
    isDeafened: boolean;
    small?: boolean;
    isScreenShareItem?: boolean;
    onClick?: () => void;
}) => {
    const [shape, setShape] = useState<'circle' | 'tall' | 'wide'>(() => {
        if (isScreenShareItem) {
            if (participantStream) {
                const track = participantStream.getVideoTracks()[0];
                const settings = track?.getSettings();
                if (settings?.width && settings?.height) {
                    return settings.width >= settings.height ? 'wide' : 'tall';
                }
            }
            return (p as any).isMobile ? 'tall' : 'wide';
        }
        return 'circle';
    });

    const handleCardClick = (e: React.MouseEvent) => {
        // Prevent click if we are clicking a button inside the card
        if ((e.target as HTMLElement).closest('button')) {
            return;
        }
        setShape(prev => {
            if (prev === 'circle') return 'tall';
            if (prev === 'tall') return 'wide';
            return 'circle';
        });
    };

    const getExactModalBg = (color: string, opacityVal: number) => {
        if (!color) return `rgba(26, 26, 46, ${opacityVal})`;
        if (color.startsWith('rgba')) return color.replace(/[\d.]+\)$/, `${opacityVal})`);
        if (color.startsWith('rgb')) return color.replace('rgb', 'rgba').replace(')', `, ${opacityVal})`);
        let hex = color.replace('#', '');
        if (hex.length === 3 || hex.length === 4) hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
        const r = parseInt(hex.substring(0, 2), 16);
        const g = parseInt(hex.substring(2, 4), 16);
        const b = parseInt(hex.substring(4, 6), 16);
        return (!isNaN(r) && !isNaN(g) && !isNaN(b)) ? `rgba(${r}, ${g}, ${b}, ${opacityVal})` : `rgba(26, 26, 46, ${opacityVal})`;
    };

    const userOpacity = (userData?.postBlurOpacity !== undefined ? userData.postBlurOpacity : 50) / 100;
    const cardBg = (shape === 'circle' && !isScreenShareItem) ? '#111827' : getExactModalBg(activeThemeColor, userOpacity);

    const outerGridClasses = (!small && isScreenShareItem)
        ? (shape === 'wide' ? 'col-span-2 row-span-2 sm:col-span-2 md:col-span-3 md:row-span-2 self-stretch' : 'col-span-2 row-span-3 sm:col-span-2 md:col-span-2 md:row-span-3 self-stretch')
        : '';

    let sizeClasses = '';
    if (isScreenShareItem) {
        if (small) {
            // 4x larger than small participant bubble (w-10 h-10)
            sizeClasses = shape === 'wide' ? 'w-32 h-18' : (shape === 'tall' ? 'w-18 h-32' : 'w-20 h-20');
        } else {
            // Fills the multi-slot grid area allocated by the outer grid container
            sizeClasses = 'w-full h-full min-h-[14rem] min-w-[12rem]';
        }
    } else {
        if (small) {
            if (shape === 'circle') sizeClasses = 'w-10 h-10';
            else if (shape === 'tall') sizeClasses = 'w-10 h-14';
            else sizeClasses = 'w-14 h-10';
        } else {
            if (shape === 'circle') sizeClasses = 'w-24 h-24';
            else if (shape === 'tall') sizeClasses = 'w-24 h-36';
            else sizeClasses = 'w-36 h-24';
        }
    }

    const borderRadiusClass = (shape === 'circle' && !isScreenShareItem) ? 'rounded-full' : 'rounded-[1.5rem]';

    return (
        <div className={`flex flex-col items-center gap-2 animate-fade-in group ${small ? 'w-auto' : ''} ${outerGridClasses}`}>
            <div
                onClick={handleCardClick}
                className={`relative ${sizeClasses} border-${small ? '2' : '4'} ${borderRadiusClass} overflow-hidden transition-all duration-300 transform group-hover:scale-[1.05] ${!isScreenShareItem && p.isSpeaking ? 'scale-110' : 'scale-100'} cursor-pointer ${shape !== 'circle' ? 'backdrop-blur-sm shadow-lg' : ''}`}
                style={{ 
                    borderColor: !isScreenShareItem && p.isSpeaking ? activeThemeColor : 'rgba(255,255,255,0.1)',
                    boxShadow: !isScreenShareItem && p.isSpeaking ? `0 0 20px ${activeThemeColor}44` : 'none',
                    backgroundColor: cardBg,
                    isolation: 'isolate',
                    WebkitMaskImage: shape === 'circle' ? '-webkit-radial-gradient(white, black)' : undefined
                }}
            >
                {hasVideo && participantStream ? (
                    <RemoteStreamView
                        stream={participantStream}
                        isLocal={p.username === currentUser}
                        className={`w-full h-full object-cover ${borderRadiusClass}`}
                        onAspectRatioChange={(ratio) => {
                            if (isScreenShareItem && ratio && !isNaN(ratio)) {
                                setShape(ratio >= 1 ? 'wide' : 'tall');
                            }
                        }}
                        style={{
                            WebkitMaskImage: (shape === 'circle' && !isScreenShareItem) ? '-webkit-radial-gradient(white, black)' : undefined
                        }}
                    />
                ) : isScreenShareItem ? (
                    <div className="w-full h-full flex flex-col items-center justify-center bg-gray-900/90 text-green-400 p-2 text-center">
                        <ScreenShareIcon className="w-6 h-6 animate-pulse mb-1" />
                        <span className="text-[9px] font-black uppercase tracking-tight">Screen</span>
                    </div>
                ) : (
                    userData.pfp ? (
                        <img
                            src={userData.pfp}
                            alt={p.username}
                            className="w-full h-full object-cover"
                        />
                    ) : (
                        <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-800 to-gray-900 text-white text-3xl font-bold">
                            {p.username[0]?.toUpperCase()}
                        </div>
                    )
                )}

                {/* Status Indicators Overlay */}
                <div className={`absolute inset-0 pointer-events-none ring-inset ring-1 ring-white/10 ${borderRadiusClass}`} />

                {/* Fullscreen Button */}
                {hasVideo && (
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            if (onClick) onClick();
                        }}
                        className="absolute bottom-1 right-1 p-1 bg-black/60 hover:bg-black text-white rounded-lg border border-white/20 transition-all active:scale-90 z-20 flex items-center justify-center"
                        title="Focus Video"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M4 8V4h4M20 8V4h-4M4 16v4h4M20 16v4h-4" />
                        </svg>
                    </button>
                )}
            </div>

            {!small && (
                <div className="flex flex-col items-center gap-1">
                    <div className="flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-black/40">
                        <span className="text-[10px] font-black text-white/90 truncate max-w-[95px] uppercase tracking-wider">
                            {isScreenShareItem ? `${userData.displayName || p.username}'s Screen` : (userData.displayName || p.username)}
                        </span>
                        {!isScreenShareItem && p.isDeafened && (
                            <svg className="w-2.5 h-2.5 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M3 18v-6a9 9 0 0118 0v6M4 19a2 2 0 002-2v-3a2 2 0 00-2-2H3v7h1zm16 0h1v-7h-1a2 2 0 00-2 2v3a2 2 0 002 2z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M3 3l18 18" />
                            </svg>
                        )}
                        {!isScreenShareItem && p.isMuted && !p.isDeafened && <VolumeOffIcon className="w-2.5 h-2.5 text-red-500" />}
                    </div>
                </div>
            )}
            
            {small && !isScreenShareItem && (p.isMuted || p.isDeafened) && (
                <div className="absolute -bottom-1 -right-1 bg-black/80 rounded-full p-0.5 border border-white/10 z-10 flex gap-0.5">
                    {p.isDeafened ? (
                        <svg className="w-2.5 h-2.5 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M3 18v-6a9 9 0 0118 0v6M4 19a2 2 0 002-2v-3a2 2 0 00-2-2H3v7h1zm16 0h1v-7h-1a2 2 0 00-2 2v3a2 2 0 002 2z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M3 3l18 18" />
                        </svg>
                    ) : (
                        <VolumeOffIcon className="w-2.5 h-2.5 text-red-500" />
                    )}
                </div>
            )}
        </div>
    );
});
ParticipantCard.displayName = 'ParticipantCard';

const DiagnosticsPanel = ({ logs, room, participants, remoteStreamsMap, currentUser, micError, isMuted, isDeafened, onReconnect }: {
    logs: string[];
    room: Room | null;
    participants: VoiceParticipant[];
    remoteStreamsMap: Record<string, MediaStream>;
    currentUser: string;
    micError: string | null;
    isMuted: boolean;
    isDeafened: boolean;
    onReconnect: () => void;
}) => {
    const [isOpen, setIsOpen] = useState(false);

    if (!isOpen) {
        return (
            <button
                onClick={() => setIsOpen(true)}
                className="fixed bottom-4 right-4 z-[200] px-4 py-2 bg-black/85 hover:bg-black text-white text-xs font-bold rounded-full border border-white/20 shadow-lg flex items-center gap-1.5 backdrop-blur-md transition-all active:scale-95"
            >
                <span>🎙️ Call Diagnostics</span>
            </button>
        );
    }

    const connectionState = room ? room.state : 'Not initialized';
    const localAudioPub = room?.localParticipant?.isMicrophoneEnabled ? 'Enabled' : 'Disabled';
    const localVideoPub = room?.localParticipant?.isCameraEnabled ? 'Enabled' : 'Disabled';

    return (
        <div className="fixed bottom-4 right-4 z-[200] w-80 max-h-[75vh] flex flex-col bg-black/95 text-white rounded-3xl border border-white/20 shadow-2xl overflow-hidden backdrop-blur-md">
            <div className="p-4 bg-white/5 border-b border-white/10 flex items-center justify-between">
                <span className="font-black text-xs uppercase tracking-wider text-blue-400">🎙️ Call Diagnostics</span>
                <button
                    onClick={() => setIsOpen(false)}
                    className="p-1 hover:bg-white/10 rounded-full text-gray-400 hover:text-white"
                >
                    <CloseIcon className="w-4 h-4" />
                </button>
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-4 text-xs">
                {/* Connection Info */}
                <div className="space-y-1 bg-white/5 p-3 rounded-2xl border border-white/5">
                    <div className="flex justify-between"><span className="text-gray-400">LiveKit Connection:</span> <span className="font-bold">{connectionState}</span></div>
                    <div className="flex justify-between"><span className="text-gray-400">Mic Publish:</span> <span>{localAudioPub}</span></div>
                    <div className="flex justify-between"><span className="text-gray-400">Camera Publish:</span> <span>{localVideoPub}</span></div>
                    <div className="flex justify-between"><span className="text-gray-400">Muted / Deafened:</span> <span>{isMuted ? 'Muted' : 'Unmuted'} / {isDeafened ? 'Deafened' : 'Undeafened'}</span></div>
                </div>

                {/* Mic Error Banner */}
                {micError && (
                    <div className="bg-red-500/20 border border-red-500/40 p-3 rounded-2xl text-red-300 font-medium">
                        ⚠️ {micError}
                    </div>
                )}

                {/* Participant List */}
                <div className="space-y-2">
                    <span className="font-bold text-[10px] text-gray-400 uppercase tracking-widest">Active Stream Matrix</span>
                    <div className="space-y-1 max-h-24 overflow-y-auto pr-1">
                        {participants.map(p => {
                            const isLocal = p.username === currentUser;
                            const remoteStream = isLocal ? null : remoteStreamsMap[p.username.toLowerCase()];
                            const tracksCount = remoteStream ? remoteStream.getTracks().length : 0;
                            return (
                                <div key={p.socketId} className="flex justify-between items-center py-1 border-b border-white/5 last:border-0">
                                    <span className="truncate max-w-[120px] font-medium">{p.username} {isLocal && '(You)'}</span>
                                    <span className="text-[10px] text-gray-400">
                                        {isLocal ? 'Local Stream' : `WebRTC Tracks: ${tracksCount}`}
                                    </span>
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* Real-time Diagnostics Log */}
                <div className="space-y-2 flex-1 flex flex-col">
                    <span className="font-bold text-[10px] text-gray-400 uppercase tracking-widest">Live Room Events</span>
                    <div className="bg-black/50 p-2.5 rounded-2xl border border-white/5 font-mono text-[10px] max-h-36 overflow-y-auto text-gray-300 space-y-1">
                        {logs.length === 0 ? (
                            <div className="text-gray-500 italic">No events logged yet.</div>
                        ) : (
                            logs.map((log, idx) => (
                                <div key={idx} className="leading-relaxed break-words">{log}</div>
                            ))
                        )}
                    </div>
                </div>

                {/* Force Re-sync Button */}
                <button
                    onClick={onReconnect}
                    className="w-full py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-2xl font-bold transition-all text-center"
                >
                    Force Reconnect
                </button>
            </div>
        </div>
    );
};
